import { ComponentType, createContext, useContext, useMemo } from 'react';
import { LiveEventParamKeyGenerator } from '../../../../../common/eventUtils';
import { safeParseJson } from '../../../../../common/utils';
import { ILiveEventSummary, EventMode, LiveEventMode } from '../../../../../types/eventTypes';
import { useFeatureParam } from '../../../selectors/global';
import { isBracketFormatEligibleForRankings } from '../../utils/getRunRankingsData';
import { DEFAULT_LIVE_MASTHEAD_VIDEO_CONFIG } from '../constants';
import {
  TLiveMastheadComponentConfig,
  LiveMastheadComponents,
  TMastheadLayoutConfig,
  ILiveEventVideoConfig
} from '../types';

export type MastheadComponentConfigOrType = TLiveMastheadComponentConfig | LiveMastheadComponents;

export const getDefaultMastheadLayout = (data: ILiveEventSummary): MastheadComponentConfigOrType[][] => {
  if (data.event.mode === EventMode.WAITING_REGISTRATION) {
    return [[LiveMastheadComponents.Video, LiveMastheadComponents.Event], [LiveMastheadComponents.Coverage]];
  }

  if (data.event.mode === EventMode.ACTIVE) {
    return [[LiveMastheadComponents.Event]];
  }

  if (data.event.mode === EventMode.WAITING_EVENT_START) {
    return [[LiveMastheadComponents.Video, LiveMastheadComponents.Event], [LiveMastheadComponents.Coverage]];
  }

  const isLeaderBoard = data.bracket && isBracketFormatEligibleForRankings(data.bracket);

  if (isLeaderBoard) {
    return [[LiveMastheadComponents.Video, LiveMastheadComponents.Coverage], [LiveMastheadComponents.Event]];
  }

  return [[LiveMastheadComponents.Video, LiveMastheadComponents.Event], [LiveMastheadComponents.Coverage]];
};

const DEFAULT_MASTHEAD_LAYOUT_CONFIGS_MAP: Record<LiveMastheadComponents, TLiveMastheadComponentConfig> = {
  [LiveMastheadComponents.Video]: { component: LiveMastheadComponents.Video },
  [LiveMastheadComponents.Event]: { component: LiveMastheadComponents.Event, timer: 'default' },
  [LiveMastheadComponents.Coverage]: { component: LiveMastheadComponents.Coverage }
};

export const normalizeMastheadLayoutConfigs = (config: MastheadComponentConfigOrType[][]): TMastheadLayoutConfig => {
  return config.map((row) => row.map((data) => normalizeMastheadLayoutConfig(data)));
};

export const normalizeMastheadLayoutConfig = (config: MastheadComponentConfigOrType): TLiveMastheadComponentConfig => {
  const normalizedConfig = typeof config === 'object' ? config : { component: config };
  const defaultConfig = DEFAULT_MASTHEAD_LAYOUT_CONFIGS_MAP[normalizedConfig.component] as any;
  return { ...defaultConfig, ...normalizedConfig };
};

export const useLiveEventMastheadLayoutConfig = (
  liveEventMode: LiveEventMode,
  data: ILiveEventSummary
): TMastheadLayoutConfig => {
  const mastheadLayoutKey = LiveEventParamKeyGenerator.getMastheadLayoutKey(liveEventMode);
  const newMastheadLayoutString = useFeatureParam(mastheadLayoutKey);
  const videoConfig = useLiveEventVideoConfig(liveEventMode);

  const mastheadLayout = useMemo(() => {
    const defaultLayout = getDefaultMastheadLayout(data);

    const paramConfig = newMastheadLayoutString
      ? safeParseJson<MastheadComponentConfigOrType[][]>(newMastheadLayoutString, undefined) || defaultLayout
      : defaultLayout;

    let result = normalizeMastheadLayoutConfigs(paramConfig);

    const isAutoSelfScoreEnabled = data.bracket?.autoSelfScoreEnabled;

    if (videoConfig.disabled || !videoConfig.videoId || isAutoSelfScoreEnabled) {
      result = result
        .map((row) => row.filter((component) => component.component !== LiveMastheadComponents.Video))
        .filter((row) => row.length > 0);
    }

    return result;
  }, [newMastheadLayoutString, data, videoConfig]);

  return mastheadLayout;
};

export const useLiveEventVideoConfig = (mode: LiveEventMode) => {
  const videoConfig = { ...DEFAULT_LIVE_MASTHEAD_VIDEO_CONFIG };
  const videoConfigKey = LiveEventParamKeyGenerator.getVideoConfigKey(mode);
  const newVideoConfigString = useFeatureParam(videoConfigKey);

  return useMemo(() => {
    const newVideoConfig = newVideoConfigString
      ? safeParseJson<ILiveEventVideoConfig>(newVideoConfigString)
      : undefined;

    return { ...videoConfig, ...newVideoConfig };
  }, [newVideoConfigString]);
};

type LiveEventComponentsContextType = {
  EventsLeaderboardWidget: ComponentType<any> | null;
};

export const LiveEventComponentsContext = createContext<LiveEventComponentsContextType>({
  EventsLeaderboardWidget: null
});

export const useLiveEventComponents = () => {
  return useContext(LiveEventComponentsContext);
};
